import React, { Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { AddableModuleType } from "@formant/core/model/AddableModuleType";
import { IAnalyticsModuleParameters } from "@formant/core/model/IAnalyticsModuleParameters";
import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceDetails } from "@formant/core/model/IDeviceDetails";
import { IFilter } from "@formant/core/model/IFilter";
import { IShare } from "@formant/core/model/IShare";
import { ITeleopLayout } from "@formant/core/model/ITeleopLayout";
import { Button, Minus } from "@formant/ui-kit";
import { IconName } from "@formant/ui/component/common/Icon";

import {
    annotationTemplateStore,
    appStore,
    authStore,
    observeStore,
    organizationStore,
} from "../../stores";
import { IMenuItem } from "../common/Menu/IMenuItem";
import { MenuButton } from "../common/MenuButton";

export interface IModuleMenuProps {
    device?: IDevice | IDeviceDetails;
    editingLayout?: boolean;
    collapsed?: boolean;
    teleop?: boolean;
    onComment?: (defaultText?: string) => void;
    onDemandData?: () => void;
    onFullscreen?: () => void;
    onClose?: () => void;
    onAnnotate?: () => void;
    onConfigure?: () => void;
    onRemove?: () => void;
    onClone?: () => void;
    filter?: IFilter;
    streamName?: string;
    fullscreen?: boolean;
    share?: IShare;
    type?: AddableModuleType;
    layout: ITeleopLayout;
    customMenuItems?: { label: string; icon?: IconName; onClick: () => void }[];
    onChanged?: (openState: boolean) => void;
}

@observer
export class ModuleMenu extends Component<IModuleMenuProps> {
    @observable
    sound = false;

    onToggleSound = () => {
        this.sound = !this.sound;
        if (this.sound) {
            this.onCustomMenuTrigger("sound-on");
        } else {
            this.onCustomMenuTrigger("sound-off");
        }
    };

    onDescribe = () => {
        this.onCustomMenuTrigger("describe");
    };

    public render() {
        const { onExportTelemetry, onExportAnalytics } = this;
        const {
            device,
            editingLayout,
            teleop,
            onAnnotate,
            onDemandData,
            onFullscreen,
            onClose,
            filter,
            streamName,
            fullscreen,
            share,
            type,
            onConfigure,
            customMenuItems,
            collapsed,
            onChanged,
            onRemove,
        } = this.props;

        const isCustomModule = streamName?.startsWith("custom-module:");
        const isAnalyticsModule = streamName?.startsWith("analytics-module:");
        const canConfigure = authStore.hasAccess("views", "administrator");

        const items: IMenuItem[] = [];
        if (editingLayout) {
            if (onConfigure && (canConfigure || isCustomModule)) {
                items.push({
                    icon: "settings",
                    value: "Edit...",
                    onSelected: onConfigure,
                });
            }
        } else {
            if (onConfigure && (canConfigure || isCustomModule)) {
                items.push({
                    icon: "settings",
                    value: "Edit...",
                    onSelected: onConfigure,
                });
            }
            if (onFullscreen && !teleop) {
                items.push({
                    icon: "fullscreen",
                    value: !fullscreen ? "Fullscreen" : "Exit fullscreen",
                    onSelected: onFullscreen,
                });
            }
            if (
                !isCustomModule &&
                this.props.onComment &&
                authStore.hasAccess("comments", "operator")
            ) {
                items.push({
                    icon: "comment",
                    value: "Comment",
                    onSelected: this.onComment,
                });
            }
            if (
                device &&
                onAnnotate &&
                annotationTemplateStore.existsForDevice(device) &&
                canConfigure
            ) {
                items.push({
                    value: "Annotate",
                    icon: "annotation",
                    onSelected: this.onAnnotate,
                });
            }
        }
        if (
            !isCustomModule &&
            onDemandData &&
            authStore.featureEnabled("onDemand")
        ) {
            items.push({
                icon: "on-demand",
                value: "Retrieve on-demand data",
                onSelected: onDemandData,
            });
        }
        if (
            !isCustomModule &&
            !isAnalyticsModule &&
            !share &&
            !teleop &&
            authStore.hasAccess("streams", "viewer", "dataExport") &&
            filter &&
            streamName
        ) {
            items.push({
                icon: "download",
                value: "Export",
                onSelected: onExportTelemetry,
            });
        }
        if (
            isAnalyticsModule &&
            authStore.hasAccess("streams", "viewer", "dataExport") &&
            streamName
        ) {
            items.push({
                icon: "download",
                value: "Export",
                onSelected: onExportAnalytics,
            });
        }
        if (onClose && teleop) {
            items.push({
                icon: "remove",
                value: "Close",
                onSelected: onClose,
            });
        }
        if (type === "video") {
            items.push({
                icon: "audio",
                value: this.sound ? "Turn sound off" : "Turn sound on",
                onSelected: this.onToggleSound,
            });

            const explorationsEnabled = organizationStore.explorationsEnabled();
            if (explorationsEnabled) {
                items.push({
                    icon: "eye",
                    value: "Analyze with AI",
                    onSelected: this.onDescribe,
                });
            }
        }
        if (type === "location") {
            items.push({
                icon: "arrow-up",
                value: "Get directions to",
                onSelected: this.onCustomMenuTrigger.bind(
                    this,
                    "location-google-maps",
                ),
            });
            items.push({
                icon: "copy",
                value: "Copy location to clipboard",
                onSelected: this.onCustomMenuTrigger.bind(
                    this,
                    "location-clipboard",
                ),
            });
        }
        if (customMenuItems) {
            customMenuItems.forEach(_ => {
                items.push({
                    icon: _.icon,
                    value: _.label,
                    onSelected: _.onClick,
                });
            });
        }

        if (onRemove && canConfigure && (isCustomModule || isAnalyticsModule)) {
            items.push({
                icon: "delete",
                value: "Delete...",
                onSelected: onRemove,
            });
        }

        if (isAnalyticsModule) {
            items.push({
                icon: "clone",
                value: "Duplicate",
                onSelected: this.props.onClone,
            });
        }

        if (observeStore.configuringAnalyticsId) {
            return (
                <Button variant="ghost" size="icon" onClick={onRemove}>
                    <Minus />
                </Button>
            );
        }

        return (
            <MenuButton
                items={items}
                collapsed={collapsed}
                onChanged={onChanged}
            />
        );
    }

    private onCustomMenuTrigger = (trigger: string) => {
        const { streamName } = this.props;
        if (streamName) {
            appStore.notifyCustomMenuHandler(streamName, trigger);
        }
    };

    private onComment = (): void => {
        this.props.onComment?.();
    };

    private onAnnotate = (): void => {
        this.props.onAnnotate?.();
    };

    private onExportTelemetry = (): void => {
        const { filter, streamName } = this.props;
        observeStore.exportTelemetry({
            ...defined(filter),
            names: [defined(streamName)],
        });
    };

    private onExportAnalytics = async (): Promise<void> => {
        const { streamName, layout } = this.props;
        if (!streamName) {
            return;
        }
        const query = (
            layout?.[streamName]?.configuration
                ?.parameters as IAnalyticsModuleParameters
        )?.query;

        if (query) {
            observeStore.exportAnalyticsPopupQuery = query;
        }
    };
}
